export const vertexShader = `#version 300 es
  in vec2 position;
  
  void main() {
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

export const fragmentShader = `#version 300 es
  precision lowp float;
  
  uniform vec2 resolution;
  uniform float time;
  
  out vec4 fragColor;
  
  #define PI 3.14159265359
  
  // Gradient colors - reduced from 6 to 4 colors for better performance while maintaining visual quality
  const vec3 gradientColors[4] = vec3[4](
    vec3(0.95, 0.78, 1.00),  // Vibrant purple
    vec3(0.60, 0.80, 1.00),  // Electric blue
    vec3(0.90, 0.70, 0.95),  // Deep magenta
    vec3(0.75, 0.85, 1.00)   // Bright blue
  );

  // Optimized random function using a simpler hash
  float random(vec2 st) {
    return fract(sin(dot(st.xy, vec2(127.1, 311.7))) * 43758.5453);
  }

  // Simplified noise using fewer operations
  float noise(vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);
    f = f * f * (3.0 - 2.0 * f); // Optimized smoothstep
    
    // Single mix operation using vec4 to reduce instructions
    vec4 h = vec4(
      random(i),
      random(i + vec2(1.0, 0.0)),
      random(i + vec2(0.0, 1.0)),
      random(i + vec2(1.0, 1.0))
    );
    
    return mix(
      mix(h.x, h.y, f.x),
      mix(h.z, h.w, f.x),
      f.y
    );
  }

  // Optimized fbm with fewer octaves
  float fbm(vec2 st) {
    float value = 0.0;
    float amplitude = 0.5;
    
    // Reduced to 3 octaves for better performance
    for(int i = 0; i < 3; i++) {
      value += amplitude * noise(st);
      st *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  // Optimized flow field calculation
  vec2 flowField(vec2 uv, float time) {
    // Precalculate trig values
    float st = sin(time * 0.3);
    float ct = cos(time * 0.2);
    
    return vec2(
      sin(uv.y * 2.0 + time) * cos(time * 0.5) + st * 0.3,
      cos(uv.x * 2.0 - time * 0.7) * sin(time * 0.4) + ct * 0.3
    );
  }

  vec2 waveMotion(vec2 uv, float time) {
    vec2 flow = flowField(uv, time);
    
    // Precalculate sine and cosine values
    float sx = sin(uv.y * 1.5 + time + flow.x);
    float cy = cos(uv.x * 1.5 + time * 0.8 + flow.y);
    float st = sin(time * 0.7);
    float ct = cos(time * 0.5);
    
    return vec2(
      sx * 0.15 + sin(uv.y + time * 0.7) * 0.2,
      cy * 0.15 + cos(uv.x - time * 0.5) * 0.2
    );
  }

  vec3 createBackground(vec2 uv) {
    float slowTime = time * 0.4;
    
    // Precalculate common values
    float st = sin(slowTime);
    float ct = cos(slowTime * 0.7);
    
    vec2 flow = flowField(uv, slowTime);
    vec2 wave = waveMotion(uv + flow * 0.5, slowTime);
    
    // Combine distortions in one step
    vec2 distortedUV = uv + wave * 1.5 + flow * 0.3;
    distortedUV.y += st * 0.3 + ct * 0.2;
    
    // Precalculate common angles and values for centers
    float baseAngle = slowTime;
    vec2 centers[4];
    float weights[4];
    float totalWeight = 0.0;
    
    for(int i = 0; i < 4; i++) {
      float angle = float(i) * PI / 2.0 + baseAngle * (1.0 + float(i) * 0.2);
      float radius = 1.5 + st * 0.5;
      
      // Calculate center position
      float cx = cos(angle);
      float sx = sin(angle);
      centers[i] = vec2(
        cx * radius + sx * 0.4,
        sx * radius + ct * 0.4
      ) + flow * 0.5;
      
      // Calculate weight in same loop
      float dist = length(distortedUV - centers[i]);
      weights[i] = 1.0 / (1.0 + dist * dist * 0.4);
      weights[i] *= 1.0 + st * 0.6;
      totalWeight += weights[i];
    }
    
    // Single pass color calculation
    vec3 color = vec3(0.0);
    for(int i = 0; i < 4; i++) {
      weights[i] /= totalWeight;
      color += gradientColors[i] * weights[i];
    }
    
    // Simplified noise calculation
    float noise = fbm(distortedUV * 3.0 + flow * 2.0) * 0.15;
    vec3 noiseColor = mix(
      vec3(0.85, 0.70, 1.00),
      vec3(1.00, 0.80, 0.90),
      noise + dot(flow, vec2(0.5))
    );
    
    return color + noise * noiseColor + vec3(dot(flow, vec2(0.1))) * 0.1;
  }
  
  void main() {
    vec2 uv = (2.0 * gl_FragCoord.xy - resolution.xy) / min(resolution.x, resolution.y);
    vec3 color = createBackground(uv);
    fragColor = vec4(color, 1.0);
  }
`;
